/**
 * Preprocess news markdown before @nuxt/content parse.
 * Converts author-friendly <game-item>, <item-grid>, <inline-tooltip>,
 * <gear-builder>, and <video> HTML tags to MDC components.
 * Also turns standalone YouTube / .mp4 URLs into embeds.
 *
 * Tags on their own line become inline MDC (no extra line-break markup).
 * Inline tags mid-sentence stay inline.
 */

const GAME_ITEM_HTML_RE = /<game-item(\s[^>]*)?>([\s\S]*?)<\/game-item>/gi
const GAME_ITEM_STANDALONE_RE = /^[ \t]*<game-item(\s[^>]*)?>([\s\S]*?)<\/game-item>[ \t]*$/gm
const ITEM_GRID_HTML_RE = /<item-grid(\s[^>]*)?>([\s\S]*?)<\/item-grid>/gi
const ITEM_GRID_STANDALONE_RE = /^[ \t]*<item-grid(\s[^>]*)?>([\s\S]*?)<\/item-grid>[ \t]*$/gm
const INLINE_TOOLTIP_SELF_RE = /<inline-tooltip(\s[^>]*)?\/>/gi
const INLINE_TOOLTIP_HTML_RE = /<inline-tooltip(\s[^>]*)?>([\s\S]*?)<\/inline-tooltip>/gi
const GEAR_BUILDER_HTML_RE = /<gear-builder(\s[^>]*)?>([\s\S]*?)<\/gear-builder>/gi
const VIDEO_HTML_RE = /<video\b[^>]*>[\s\S]*?<\/video>/gi

/** Extract unique positive item IDs from free-form text (comma, space, or newline separated). */
export function parseItemIdList(text: string): number[] {
  if (!text?.trim()) return []

  const seen = new Set<number>()
  const ids: number[] = []

  for (const match of text.matchAll(/\d+/g)) {
    const id = Number(match[0])
    if (id > 0 && !seen.has(id)) {
      seen.add(id)
      ids.push(id)
    }
  }

  return ids
}

export interface ItemGridEntry {
  id: number
  price?: number
}

/** Parse `items` MDC prop: `19400:100,19398:200` or legacy `19400,19398`. */
export function parseItemGridItemsProp(items: string): ItemGridEntry[] {
  if (!items?.trim()) return []

  const seen = new Set<number>()
  const entries: ItemGridEntry[] = []

  for (const part of items.split(',')) {
    const trimmed = part.trim()
    if (!trimmed) continue

    const [idStr, priceStr] = trimmed.split(':')
    const id = Number(idStr)
    if (!id || id <= 0 || seen.has(id)) continue

    seen.add(id)
    const price = priceStr !== undefined && priceStr !== '' ? Number(priceStr) : undefined
    entries.push({
      id,
      price: price != null && !Number.isNaN(price) ? price : undefined,
    })
  }

  return entries
}

function parseCommaSeparatedValues(text: string): ItemGridEntry[] {
  const parts = text.split(/\s*,\s*/).filter(Boolean)
  if (!parts.length) return []

  if (parts.length === 2) {
    const id = Number(parts[0])
    const second = Number(parts[1])
    if (id > 0 && second > 0) {
      // `19400,100` → item + price; `52631,9001013` → two item ids.
      if (second < id) return [{ id, price: second }]
      return [{ id }, { id: second }]
    }
  }

  if (parts.length >= 4 && parts.length % 2 === 0) {
    const entries: ItemGridEntry[] = []
    let looksLikePairs = true
    for (let i = 0; i < parts.length; i += 2) {
      const id = Number(parts[i])
      const price = Number(parts[i + 1])
      if (!(id > 0 && price > 0 && price < id)) {
        looksLikePairs = false
        break
      }
      entries.push({ id, price })
    }
    if (looksLikePairs) return entries
  }

  return parseItemIdList(text).map((id) => ({ id }))
}

function parseItemGridLine(line: string): ItemGridEntry[] {
  const trimmed = line.trim()
  if (!trimmed) return []
  return parseCommaSeparatedValues(trimmed)
}

/** Parse item-grid body lines such as `19400,100` or `19400`. */
export function parseItemGridEntries(text: string): ItemGridEntry[] {
  if (!text?.trim()) return []

  const seen = new Set<number>()
  const entries: ItemGridEntry[] = []

  for (const line of text.split(/\n+/)) {
    for (const entry of parseItemGridLine(line)) {
      if (seen.has(entry.id)) continue
      seen.add(entry.id)
      entries.push(entry)
    }
  }

  return entries
}

function mergeItemGridEntries(...lists: ItemGridEntry[][]): ItemGridEntry[] {
  const byId = new Map<number, ItemGridEntry>()
  for (const list of lists) {
    for (const entry of list) {
      const existing = byId.get(entry.id)
      byId.set(entry.id, {
        id: entry.id,
        price: entry.price ?? existing?.price,
      })
    }
  }
  return [...byId.values()]
}

function entriesToMdcProp(entries: ItemGridEntry[]): string {
  return entries
    .map((e) => (e.price != null ? `${e.id}:${e.price}` : `${e.id}`))
    .join(',')
}

function parseGameItemAttrs(attrsStr: string): {
  id?: string
  modelOnly?: boolean
  model?: boolean
} {
  const idMatch = attrsStr.match(/\bid\s*=\s*["']?(\d+)["']?/i)
  const modelOnly = /\b(?:only-model|model-only)\b/i.test(attrsStr)
  const model = !modelOnly && /\b(?:model|both)\b/i.test(attrsStr)
  return { id: idMatch?.[1], modelOnly, model }
}

function escapeMdcSlot(text: string): string {
  return text.replace(/]/g, '\\]')
}

function buildMdcProps(id: string, modelOnly: boolean, model: boolean): string {
  const props: string[] = [`id="${id}"`]
  if (modelOnly) props.push('model-only')
  else if (model) props.push('model')
  return props.join(' ')
}

function htmlTagToMdcInline(attrsStr: string, label: string): string {
  const { id, modelOnly, model } = parseGameItemAttrs(attrsStr || '')
  if (!id) return label.trim() || ''

  const props = buildMdcProps(id, !!modelOnly, !!model)
  const trimmed = label.trim()
  if (trimmed) {
    return `:game-item[${escapeMdcSlot(trimmed)}]{${props}}`
  }
  return `:game-item{${props}}`
}

function htmlTagToMdcStandalone(attrsStr: string, label: string): string {
  return htmlTagToMdcInline(attrsStr, label)
}

function parseItemGridAttrs(attrsStr: string): { ids?: string; genderToggle?: boolean } {
  const idsMatch = attrsStr.match(/\bids\s*=\s*["']([^"']+)["']/i)
  const genderToggle = /\b(?:gender-toggle|gender_toggle)\b/i.test(attrsStr || '')
  return { ids: idsMatch?.[1]?.trim(), genderToggle }
}

function htmlItemGridToMdc(attrsStr: string, body: string): string {
  const { ids: attrIds, genderToggle } = parseItemGridAttrs(attrsStr || '')
  const attrEntries = attrIds ? parseItemGridEntries(attrIds.replace(/\n/g, ',')) : []
  const bodyEntries = parseItemGridEntries(String(body || ''))
  const merged = mergeItemGridEntries(attrEntries, bodyEntries)
  if (!merged.length) return ''

  const props = [`items="${entriesToMdcProp(merged)}"`]
  if (genderToggle) props.push('gender-toggle')
  return `:item-grid{${props.join(' ')}}`
}

/** Collapse blank lines that authors add between standalone item tags. */
function collapseStandaloneSpacing(content: string): string {
  return content.replace(/\n{3,}/g, '\n\n')
}

const YOUTUBE_ID_RE = /^[\w-]{11}$/

export function isValidYoutubeVideoId(id: string | undefined | null): boolean {
  return !!id && YOUTUBE_ID_RE.test(id)
}

/** Extract an 11-char YouTube video id from common watch/share/embed/shorts URLs. */
export function extractYoutubeVideoId(input: string): string | null {
  if (!input?.trim()) return null

  let raw = input.trim()
  // Strip common trailing punctuation from pasted prose.
  raw = raw.replace(/[),.]+$/g, '')

  try {
    const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
    const url = new URL(normalized)
    const host = url.hostname.replace(/^(?:www|m)\./i, '').toLowerCase()

    if (host === 'youtu.be') {
      const id = url.pathname.split('/').filter(Boolean)[0]?.split('?')[0]
      return isValidYoutubeVideoId(id) ? id! : null
    }

    if (host === 'youtube.com' || host === 'music.youtube.com') {
      const fromQuery = url.searchParams.get('v')
      if (isValidYoutubeVideoId(fromQuery)) return fromQuery!

      const parts = url.pathname.split('/').filter(Boolean)
      if (
        parts.length >= 2
        && ['embed', 'shorts', 'live', 'v'].includes(parts[0]!.toLowerCase())
      ) {
        const id = parts[1]!.split('?')[0]
        if (isValidYoutubeVideoId(id)) return id
      }
    }
  } catch {
    return null
  }

  return null
}

function youtubeMdc(id: string): string {
  return `:youtube{id="${id}"}`
}

/** Resolve a safe http(s) or root-relative .mp4 URL for news video embeds. */
export function extractMp4VideoSrc(input: string): string | null {
  if (!input?.trim()) return null

  let raw = input.trim()
  // Strip common trailing punctuation from pasted prose.
  raw = raw.replace(/[),.]+$/g, '')

  // Root-relative site paths: /vid/news/foo.mp4
  if (raw.startsWith('/') && !raw.startsWith('//')) {
    try {
      const url = new URL(raw, 'https://aa-classic.com')
      if (!/\.mp4$/i.test(url.pathname)) return null
      return `${url.pathname}${url.search}${url.hash}`
    } catch {
      return null
    }
  }

  try {
    const normalized = raw.startsWith('//')
      ? `https:${raw}`
      : /^https?:\/\//i.test(raw)
        ? raw
        : null
    if (!normalized) return null

    const url = new URL(normalized)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    if (!/\.mp4$/i.test(url.pathname)) return null
    return url.toString()
  } catch {
    return null
  }
}

function videoMdc(src: string): string {
  return `:video{src="${encodeURIComponent(src)}"}`
}

/** Convert a raw HTML <video> block to `:video{src="..."}` when an .mp4 source is found. */
export function htmlVideoToMdc(html: string): string {
  const sourceSrc = html.match(/<source\b[^>]*\bsrc\s*=\s*["']([^"']+)["']/i)?.[1]
  const videoSrc = html.match(/<video\b[^>]*\bsrc\s*=\s*["']([^"']+)["']/i)?.[1]
  const src = extractMp4VideoSrc(sourceSrc || videoSrc || '')
  if (!src) return html
  return videoMdc(src)
}

const INLINE_TOOLTIP_HIDE_ATTR_RE = /\bhide-([a-z0-9_-]+)\b/gi

function parseInlineTooltipHtmlAttrs(attrsStr: string): {
  id?: string
  grade?: string
  hide: string[]
} {
  const idMatch = attrsStr.match(/\bid\s*=\s*["']?(\d+)["']?/i)
  const gradeMatch = attrsStr.match(/\bgrade\s*=\s*["']?(\d+)["']?/i)
  const hideListMatch = attrsStr.match(/\bhide\s*=\s*["']([^"']+)["']/i)
  const hide: string[] = []

  if (hideListMatch?.[1]) {
    for (const part of hideListMatch[1].split(/[\s,]+/)) {
      const key = part.trim()
      if (key) hide.push(key)
    }
  }

  for (const match of attrsStr.matchAll(INLINE_TOOLTIP_HIDE_ATTR_RE)) {
    const key = match[1]?.trim()
    if (key) hide.push(key)
  }

  return {
    id: idMatch?.[1],
    grade: gradeMatch?.[1],
    hide: [...new Set(hide)],
  }
}

/** Convert `<inline-tooltip>` HTML (optional JSON body) to MDC block syntax. */
export function htmlInlineTooltipToMdc(attrsStr: string, body = ''): string {
  const { id, grade, hide } = parseInlineTooltipHtmlAttrs(attrsStr || '')
  const props: string[] = []
  if (id) props.push(`id="${id}"`)
  if (grade) props.push(`grade="${grade}"`)
  if (hide.length) props.push(`hide="${hide.join(',')}"`)

  const trimmedBody = String(body || '').trim()
  if (trimmedBody) {
    props.push(`payload="${encodeURIComponent(trimmedBody)}"`)
  }

  if (!props.length) return ''
  return `::inline-tooltip{${props.join(' ')}}\n::`
}

/** Convert `<gear-builder>` HTML (JSON body) to MDC block syntax. */
export function htmlGearBuilderToMdc(_attrsStr: string, body = ''): string {
  const trimmedBody = String(body || '').trim()
  if (!trimmedBody) return ''
  return `::gear-builder{payload="${encodeURIComponent(trimmedBody)}"}\n::`
}

/**
 * Turn standalone YouTube URLs (or markdown links to them) into `:youtube` embeds.
 * Skips fenced code blocks. Inline URLs mid-sentence stay as normal links.
 */
export function embedStandaloneYoutubeUrls(content: string): string {
  if (!content) return content
  if (!/(?:youtube\.com|youtu\.be)/i.test(content)) return content

  const lines = content.split('\n')
  let inFence = false

  return lines
    .map((line) => {
      if (/^\s*(`{3,}|~{3,})/.test(line)) {
        inFence = !inFence
        return line
      }
      if (inFence) return line

      const trimmed = line.trim()
      if (!trimmed) return line

      const bareId = !/\s/.test(trimmed) ? extractYoutubeVideoId(trimmed) : null
      if (bareId) return youtubeMdc(bareId)

      const mdLink = trimmed.match(/^\[([^\]]*)\]\(([^)\s]+)\)$/)
      if (mdLink) {
        const linkId = extractYoutubeVideoId(mdLink[2]!)
        if (linkId) return youtubeMdc(linkId)
      }

      return line
    })
    .join('\n')
}

/**
 * Turn standalone .mp4 URLs (or markdown links to them) into `:video` embeds.
 * Skips fenced code blocks. Inline URLs mid-sentence stay as normal links.
 */
export function embedStandaloneMp4Urls(content: string): string {
  if (!content) return content
  if (!/\.mp4(?:\b|\?|#|$)/i.test(content)) return content

  const lines = content.split('\n')
  let inFence = false

  return lines
    .map((line) => {
      if (/^\s*(`{3,}|~{3,})/.test(line)) {
        inFence = !inFence
        return line
      }
      if (inFence) return line

      const trimmed = line.trim()
      if (!trimmed) return line

      const bareSrc = !/\s/.test(trimmed) ? extractMp4VideoSrc(trimmed) : null
      if (bareSrc) return videoMdc(bareSrc)

      const mdLink = trimmed.match(/^\[([^\]]*)\]\(([^)\s]+)\)$/)
      if (mdLink) {
        const linkSrc = extractMp4VideoSrc(mdLink[2]!)
        if (linkSrc) return videoMdc(linkSrc)
      }

      return line
    })
    .join('\n')
}

/** Convert `<game-item>`, `<item-grid>`, `<inline-tooltip>`, and `<gear-builder>` HTML tags to Nuxt Content MDC syntax. Also formats `Q.` / `A.` pairs. */
export function preprocessNewsMarkdown(content: string): string {
  if (!content) return content

  let out = content

  if (out.includes('gear-builder')) {
    out = out.replace(GEAR_BUILDER_HTML_RE, (_, attrsStr, body) =>
      htmlGearBuilderToMdc(String(attrsStr || ''), String(body || '')),
    )
  }

  if (out.includes('inline-tooltip')) {
    out = out.replace(INLINE_TOOLTIP_SELF_RE, (_, attrsStr) =>
      htmlInlineTooltipToMdc(String(attrsStr || '')),
    )
    out = out.replace(INLINE_TOOLTIP_HTML_RE, (_, attrsStr, body) =>
      htmlInlineTooltipToMdc(String(attrsStr || ''), String(body || '')),
    )
  }

  if (out.includes('item-grid')) {
    out = out.replace(ITEM_GRID_STANDALONE_RE, (_, attrsStr, body) =>
      htmlItemGridToMdc(String(attrsStr || ''), String(body || '')),
    )
    out = out.replace(ITEM_GRID_HTML_RE, (_, attrsStr, body) =>
      htmlItemGridToMdc(String(attrsStr || ''), String(body || '')),
    )
  }

  if (out.includes('game-item')) {
    out = out.replace(GAME_ITEM_STANDALONE_RE, (_, attrsStr, label) =>
      htmlTagToMdcStandalone(String(attrsStr || ''), String(label || '')),
    )
    out = out.replace(GAME_ITEM_HTML_RE, (_, attrsStr, label) =>
      htmlTagToMdcInline(String(attrsStr || ''), String(label || '')),
    )
  }

  if (/<video\b/i.test(out)) {
    out = out.replace(VIDEO_HTML_RE, (html) => htmlVideoToMdc(html))
  }

  out = embedStandaloneYoutubeUrls(out)
  out = embedStandaloneMp4Urls(out)
  out = formatNewsQaBlocks(out)

  return collapseStandaloneSpacing(out)
}

const QA_FENCE_RE = /^\s*(`{3,}|~{3,})/
const QA_HEADING_RE = /^\s{0,3}#{1,6}\s/
const QA_LIST_RE = /^\s{0,3}(?:[-*+]|\d{1,3}\.)\s+/
const QA_QUESTION_RE = /^Q\.\s+(\S.*)$/
const QA_ANSWER_RE = /^A\.\s+(.*)$/

function normalizeMdLine(line: string): string {
  return line.replace(/\r$/, '')
}

function isQaFenceLine(line: string): boolean {
  return QA_FENCE_RE.test(normalizeMdLine(line))
}

function isQaHeadingLine(line: string): boolean {
  return QA_HEADING_RE.test(normalizeMdLine(line))
}

function isQaQuestionLine(line: string): boolean {
  return QA_QUESTION_RE.test(normalizeMdLine(line).trim())
}

function isQaListLine(line: string): boolean {
  return QA_LIST_RE.test(normalizeMdLine(line))
}

function joinQaAnswer(start: string, extra: string[]): string {
  const answerStart = start.trim()
  if (!extra.length) return answerStart
  if (answerStart && isQaListLine(extra[0]!)) {
    return `${answerStart}\n\n${extra.join('\n')}`.trim()
  }
  return [start, ...extra].join('\n').trim()
}

function collectQaContinuation(lines: string[], start: number): { extra: string[]; nextIndex: number } {
  const extra: string[] = []
  let i = start

  while (i < lines.length) {
    const line = normalizeMdLine(lines[i]!)

    if (isQaFenceLine(line) || isQaHeadingLine(line) || isQaQuestionLine(line)) break

    if (!line.trim()) {
      let j = i + 1
      while (j < lines.length && !normalizeMdLine(lines[j]!).trim()) j++
      const peek = j < lines.length ? normalizeMdLine(lines[j]!) : ''
      if (!peek || isQaFenceLine(peek) || isQaHeadingLine(peek) || isQaQuestionLine(peek)) break
      if (isQaListLine(peek) || /^\s+\S/.test(peek)) {
        extra.push(line)
        i++
        continue
      }
      break
    }

    extra.push(line)
    i++
  }

  return { extra, nextIndex: i }
}

function tryParseQaAt(
  lines: string[],
  index: number,
): { question: string; answer: string; nextIndex: number } | null {
  const qMatch = normalizeMdLine(lines[index] ?? '').trim().match(QA_QUESTION_RE)
  if (!qMatch) return null

  let i = index + 1
  while (i < lines.length && !normalizeMdLine(lines[i]!).trim()) i++
  if (i >= lines.length) return null

  const aMatch = normalizeMdLine(lines[i]!).trim().match(QA_ANSWER_RE)
  if (!aMatch) return null

  const { extra, nextIndex } = collectQaContinuation(lines, i + 1)
  const answer = joinQaAnswer(aMatch[1] ?? '', extra)
  if (!answer) return null

  return { question: qMatch[1]!.trim(), answer, nextIndex }
}

export function buildNewsQaMdc(question: string, answer: string): string {
  const body = answer.replace(/^\n+/, '').replace(/\n+$/, '')
  return `::news-qa{question="${encodeURIComponent(question.trim())}"}\n${body}\n::`
}

/**
 * Turn author `Q.` / `A.` pairs into `::news-qa` blocks.
 * CommonMark treats a single newline as a space, so those pairs otherwise
 * render as one dense paragraph.
 */
export function formatNewsQaBlocks(content: string): string {
  if (!content || !/^Q\./m.test(content)) return content

  const lines = content.split('\n')
  const out: string[] = []
  let inFence = false
  let i = 0

  while (i < lines.length) {
    const line = normalizeMdLine(lines[i]!)

    if (isQaFenceLine(line)) {
      inFence = !inFence
      out.push(line)
      i++
      continue
    }

    if (inFence) {
      out.push(line)
      i++
      continue
    }

    const parsed = tryParseQaAt(lines, i)
    if (parsed) {
      out.push(buildNewsQaMdc(parsed.question, parsed.answer))
      i = parsed.nextIndex
      continue
    }

    out.push(line)
    i++
  }

  return out.join('\n')
}
